import { Delete, Download } from '@mui/icons-material';
import type { UseQueryOptions } from '@tanstack/react-query';
import {
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { uniqueId } from 'lodash-es';
import {
	SovosButton,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosMenuItem,
	SovosPageContentTable,
	SovosPageLayout,
	SovosSnackbar,
} from 'mosaic-react';
import React from 'react';
import { useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import {
	deleteRateFiles,
	downloadInputRateFiles,
	downloadOutputRateFiles,
	fetchRateFileStatus,
	fetchRateFiles,
	reprocessRateFiles,
} from 'src/api/rateFile';
import EmptyPage from 'src/components/emptyPage/EmptyPage';
import type { Pagination } from 'src/interfaces/Pagination';
import type { RateFile } from 'src/interfaces/RateFile';
import type { SnackbarData } from 'src/interfaces/Snackbar';

import { getShowSnackbarData } from './helpers';

interface CustomError extends Error {
	response?: {
		data?: {
			message?: string;
		};
	};
}

const RateFilesPage: React.FC = () => {
	const queryClient = useQueryClient();

	const navigate = useNavigate();
	const location = useLocation();
	const navigationType = useNavigationType();

	const [selectedRows, setSelectedRows] = useState<RateFile[]>([]);

	const [filesToDelete, setFilesToDelete] = useState<number[]>([]);

	const [pagination, setPagination] = useState<Pagination>({
		page: 0,
		perPage: Number(sessionStorage.getItem('rateFilesPerPage')) || 20,
	});

	const queryKey = [
		'rateFiles',
		{ page: pagination.page, perPage: pagination.perPage },
	];

	const query = useQuery(
		queryKey,
		() =>
			fetchRateFiles({ page: pagination.page, perPage: pagination.perPage }),
		{
			refetchInterval: 60 * 1000,
			// Show snackbar if file finished processing
			onSuccess: (data) => {
				const lastProcessingFile = query.data?.items.find(
					(item) => item.status === 'Processing'
				);

				const lastProcessingFileCurrentData =
					lastProcessingFile &&
					data.items.find((item) => lastProcessingFile.id === item.id);

				if (
					lastProcessingFileCurrentData &&
					lastProcessingFileCurrentData.status !== 'Processing'
				) {
					setSnackbar(
						getShowSnackbarData(
							lastProcessingFileCurrentData.name,
							lastProcessingFileCurrentData.status === 'Complete'
								? 'confirmation'
								: 'error'
						)
					);
				}
			},
		}
	);

	// Poll processing files
	useQueries({
		queries:
			query.data?.items
				.filter((rateFile) => rateFile.id && rateFile.status === 'Processing')
				.map<UseQueryOptions<Awaited<ReturnType<typeof fetchRateFileStatus>>>>(
					(rateFile) => ({
						queryKey: ['rateFileProcessing', rateFile.id],
						queryFn: () => fetchRateFileStatus(rateFile.id),
						onSuccess: (data) => {
							if (data.status !== 'Processing') {
								if (data.status === 'Complete') {
									setSnackbar(
										getShowSnackbarData(rateFile.name, 'confirmation')
									);
								}

								if (data.status === 'Failed') {
									setSnackbar(getShowSnackbarData(rateFile.name, 'error'));
								}

								queryClient.setQueryData<
									Awaited<ReturnType<typeof fetchRateFiles>>
								>(queryKey, (cachedRateFiles) => {
									const processedRateFileIndex =
										cachedRateFiles?.items.findIndex(
											(rf) => rf.id === rateFile.id
										);

									if (
										cachedRateFiles &&
										processedRateFileIndex !== undefined &&
										processedRateFileIndex >= 0
									) {
										const items = [...cachedRateFiles.items];

										items[processedRateFileIndex] = {
											...items[processedRateFileIndex],
											status: data.status,
											completedDate: data.completedDate,
										};

										return { ...cachedRateFiles, items };
									}

									return cachedRateFiles;
								});
							}
						},
						refetchInterval: 5000,
					})
				) || [],
	});

	// Gets Ratefile when being redirects from create new rate file,
	// in cases where the file finishes processing before the redirect finishes.
	const createdRateFileIsComplete =
		navigationType === 'PUSH' && location.state?.savedRateFileId
			? query.data?.items.find(
					(item) =>
						item.id === location.state?.savedRateFileId &&
						(item.status === 'Complete' || item.status === 'Failed')
			  )
			: null;

	// Snackbar for when files finish processing
	const [snackbar, setSnackbar] = useState<SnackbarData>(
		// Show snackbar even if file finished processing before redirect from create new rate file
		createdRateFileIsComplete
			? createdRateFileIsComplete.status === 'Complete'
				? getShowSnackbarData(createdRateFileIsComplete.name, 'confirmation')
				: getShowSnackbarData(createdRateFileIsComplete.name, 'error')
			: {
					message: '',
					open: false,
			  }
	);

	const reprocessFileMutation = useMutation(reprocessRateFiles, {
		onMutate: async (reprocessIds) => {
			// add data to cache as soon as mutation starts

			await queryClient.cancelQueries({ queryKey });

			const cachedData =
				queryClient.getQueryData<Awaited<ReturnType<typeof fetchRateFiles>>>(
					queryKey
				);

			if (cachedData) {
				const originalFile = cachedData.items.find((rateFile) =>
					reprocessIds.includes(rateFile.id)
				);

				if (originalFile) {
					const reprocessedFile = {
						...originalFile,
						id: null,
						status: 'Processing',
						creationDate: '',
						// used to identify temporary file in case of error
						outputFileUuid: uniqueId('reprocess'),
					};
					queryClient.setQueriesData(
						{ queryKey },

						{
							...cachedData,
							items: [reprocessedFile, ...cachedData.items],
						}
					);

					// saves reprocessedFile in context
					return { reprocessedFile };
				}
			}
		},
		onError: (err: CustomError, data, context) => {
			// restores cache from context if error
			if (context?.reprocessedFile) {
				queryClient.setQueryData<Awaited<ReturnType<typeof fetchRateFiles>>>(
					queryKey,
					(cachedData) =>
						cachedData
							? {
									...cachedData,
									items: cachedData.items.filter(
										(item) =>
											item.outputFileUuid !==
											context?.reprocessedFile.outputFileUuid
									),
							  }
							: cachedData
				);
			}

			setSnackbar({
				message:
					err?.response?.data?.message || 'Unable to reprocess rate file',
				open: true,
				variant: 'error',
			});
		},
		onSuccess: (data, params, context) => {
			// updates cached data with id after mutation success
			if (context?.reprocessedFile) {
				const cachedData =
					queryClient.getQueryData<Awaited<ReturnType<typeof fetchRateFiles>>>(
						queryKey
					);

				if (cachedData) {
					const items = cachedData?.items.slice();
					const reprocessedIndex = items.findIndex(
						(item) =>
							item.outputFileUuid === context?.reprocessedFile.outputFileUuid
					);
					items[reprocessedIndex] = {
						...items[reprocessedIndex],
						id: data[0].id,
					};

					queryClient.setQueryData<Awaited<ReturnType<typeof fetchRateFiles>>>(
						queryKey,
						{ ...cachedData, items: items }
					);
				}
			}
		},
		onSettled: () => {
			// refetch rate files after mutation
			queryClient.invalidateQueries({ queryKey });
		},
	});

	const downloadInputFileMutation = useMutation(downloadInputRateFiles, {
		onSuccess: () => {
			setSelectedRows([]);
		},
	});

	const downloadOutputFileMutation = useMutation(downloadOutputRateFiles, {
		onSuccess: () => {
			setSelectedRows([]);
		},
	});

	const deleteFileMutation = useMutation(() => deleteRateFiles(filesToDelete), {
		// add data to cache as soon as mutation starts
		onMutate: async () => {
			await queryClient.cancelQueries({ queryKey });

			const previous = queryClient.getQueryData(queryKey);

			queryClient.setQueryData<Awaited<ReturnType<typeof fetchRateFiles>>>(
				queryKey,
				(cachedData) =>
					cachedData
						? {
								pageState: {
									...cachedData.pageState,
									totalEntries:
										cachedData.pageState.totalEntries - filesToDelete.length,
								},
								items: cachedData.items.filter(
									(item) => !filesToDelete.includes(item.id)
								),
						  }
						: cachedData
			);

			setFilesToDelete([]);
			setSelectedRows([]);

			return { previous };
		},
		onError: (err, data, context) => {
			// restore previous cache if error
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context?.previous);
			}
		},
		onSettled: () => {
			// refetch rate files after mutation
			queryClient.invalidateQueries({ queryKey });
			setSelectedRows([]);
		},
	});

	return (
		<SovosPageLayout
			pageTitle='Rate Files'
			titleBarActions={[
				<SovosButton key='new' onClick={() => navigate('./new')}>
					Create new
				</SovosButton>,
			]}
		>
			{query?.data?.items.length === 0 && !query?.isLoading ? (
				<EmptyPage
					title='Create a rate file'
					subtitle='To get the latest tax rates, create a rate file.'
					titleButton='Create'
				/>
			) : (
				<SovosPageContentTable
					isLoading={query.isLoading}
					PaginationFooterProps={{
						itemsPerPage: query.data?.pageState.perPage,
						currentPage: pagination.page + 1,
						totalItems: query.data?.pageState.totalEntries,
						totalPages: query.data?.pageState.totalPages,
						onPageChange: (newPage: number, newItemsPerPage: number) => {
							sessionStorage.setItem(
								'rateFilesPerPage',
								newItemsPerPage.toString()
							);
							setPagination({
								page: newPage - 1,
								perPage: newItemsPerPage,
							});
						},
					}}
					TableProps={{
						columns: [
							{
								id: 'name',
								dataKey: 'name',
								title: 'File Name',
								notSortable: true,
							},
							{
								id: 'status',
								dataKey: 'status',
								title: 'Status',
								notSortable: true,
								statuses: {
									Complete: 'success',
									Failed: 'error',
									Processing: 'warning',
								},
							},
							{
								id: 'creationDate',
								dataKey: 'creationDate',
								title: 'Created Date',
								notSortable: true,
							},
							{
								id: 'completedDate',
								dataKey: 'completedDate',
								title: 'Completed Date',
								notSortable: true,
							},
							{
								id: 'outputFormatName',
								dataKey: 'outputFormatName',
								title: 'Format',
								notSortable: true,
							},
						],
						rowActions: (row: RateFile) => {
							const menuItems = {
								reprocess: (
									<SovosMenuItem
										key='reprocess'
										onClick={() => reprocessFileMutation.mutate([row.id])}
									>
										Reprocess file
									</SovosMenuItem>
								),
								downloadOutput: (
									<SovosMenuItem
										key='downloadRateFile'
										onClick={() => downloadOutputFileMutation.mutate([row.id])}
									>
										Download rate file
									</SovosMenuItem>
								),
								downloadInput: (
									<SovosMenuItem
										key='downloadUploadedFile'
										onClick={() => downloadInputFileMutation.mutate([row.id])}
									>
										Download uploaded file
									</SovosMenuItem>
								),
								delete: (
									<SovosMenuItem
										key='delete'
										onClick={() => setFilesToDelete([row.id])}
									>
										Delete file
									</SovosMenuItem>
								),
							};

							if (!row.addressFileId && row.status === 'Processing') {
								return [];
							}

							if (row.status === 'Processing') {
								return [menuItems.downloadInput];
							}

							if (!row.addressFileId) {
								return [
									menuItems.reprocess,
									menuItems.downloadOutput,
									menuItems.delete,
								];
							}

							return [
								menuItems.reprocess,
								menuItems.downloadOutput,
								menuItems.downloadInput,
								menuItems.delete,
							];
						},
						data: query.data?.items || [],
					}}
					onRowSelection={{
						isRowSelected: (row: RateFile) => {
							return !!selectedRows.find((r) => r.id === row.id);
						},
						toggleRowSelection: (row: RateFile) => {
							const selectedRow = selectedRows.find((r) => r.id === row.id);

							if (selectedRows.length > 0) {
								if (selectedRow) {
									setSelectedRows(
										selectedRows.filter((r) => r.id !== selectedRow.id)
									);
								} else {
									setSelectedRows([...selectedRows, row]);
								}
							} else {
								setSelectedRows([row]);
							}
						},
						areAllRowsSelected: () => {
							return (
								query.data && query.data.items.length === selectedRows.length
							);
						},
						areAnyRowsSelected: () => selectedRows.length > 0,
						toggleSelectAll: () => {
							if (selectedRows.length === query.data?.items.length) {
								setSelectedRows([]);
							} else if (query.data?.items) {
								setSelectedRows(query.data.items);
							}
						},
						selectedCount: selectedRows.length,
					}}
					bulkActions={[
						{
							icon: <Download />,
							onClick: () =>
								downloadOutputFileMutation.mutate(
									selectedRows.map((row) => row.id)
								),
							tooltipText: 'Download rate files',
						},
						{
							icon: <Delete />,
							onClick: () =>
								setFilesToDelete(selectedRows.map((row) => row.id)),
							tooltipText: 'Delete rate files',
						},
					]}
				/>
			)}

			{/* RateFile finished processing Snackbar */}
			<SovosSnackbar
				open={snackbar.open}
				onClose={() => setSnackbar({ ...snackbar, message: '', open: false })}
				variant={snackbar.variant}
				message={snackbar.message}
				timeout='short'
			/>

			{/* Delete Dialog */}
			<SovosDialog
				open={filesToDelete && filesToDelete.length > 0}
				onClose={() => setFilesToDelete([])}
			>
				<SovosDialogTitle showCloseButton>
					{filesToDelete.length > 1
						? `You've selected ${filesToDelete.length} rate files to delete`
						: 'Delete rate file?'}
				</SovosDialogTitle>
				<SovosDialogContent>
					{filesToDelete.length > 1
						? 'Do you want to delete the selected rate files?'
						: 'Do you want to delete this rate file?'}{' '}
					Deleting a rate file is permanent and can't be undone.
				</SovosDialogContent>
				<SovosDialogActions>
					<SovosButton variant='text' onClick={() => setFilesToDelete([])}>
						Cancel
					</SovosButton>
					<SovosButton
						color='error'
						onClick={() => deleteFileMutation.mutate()}
					>
						Delete
					</SovosButton>
				</SovosDialogActions>
			</SovosDialog>
		</SovosPageLayout>
	);
};

export default RateFilesPage;
